import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $getSelection, $isRangeSelection, $setSelection, ElementNode } from 'lexical';
import { useRef, useState } from 'react';
import { Button, TextField, Dialog, DialogTitle, DialogContent, DialogActions, Tabs, Tab, CircularProgress, Alert } from '@mui/material';
import axios from 'axios';
import url from '../../url';
import './ImagePlugin.css';

// Custom Image Node implementation
export class ImageNode extends ElementNode {
  static getType() {
    return 'image';
  }

  static clone(node) {
    return new ImageNode(node.__src, node.__altText);
  }

  constructor(src, altText) {
    super();
    this.__src = src;
    this.__altText = altText;
  }

  createDOM(config) {
    const div = document.createElement('div');
    div.style.display = 'contents';
    const img = document.createElement('img');
    img.src = this.__src;
    img.alt = this.__altText;
    img.style.maxWidth = '100%';
    div.appendChild(img);
    return div;
  }

  updateDOM() {
    return false;
  }

  static importJSON(serializedNode) {
    const node = $createImageNode(serializedNode.src, serializedNode.altText);
    return node;
  }

  static importDOM() {
    return {
      img: () => ({
        conversion: (element) => {
          const src = element.getAttribute('src');
          return src
            ? { node: $createImageNode(src, element.getAttribute('alt') || 'Image') }
            : null;
        },
        priority: 1
      })
    };
  }

  exportDOM() {
    const img = document.createElement('img');
    img.src = this.__src;
    img.alt = this.__altText;
    img.style.maxWidth = '100%';
    return { element: img };
  }

  exportJSON() {
    return {
      ...super.exportJSON(),
      src: this.__src,
      altText: this.__altText,
    };
  }
}

export function $createImageNode(src, altText = 'Image') {
  return new ImageNode(src, altText);
}

export function $isImageNode(node) {
  return node instanceof ImageNode;
}

function ImagePlugin() {
  const [editor] = useLexicalComposerContext();
  const [open, setOpen] = useState(false);
  const [imageUrl, setImageUrl] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [tabValue, setTabValue] = useState(0);
  const savedSelectionRef = useRef(null);

  const handleOpen = () => {
    editor.getEditorState().read(() => {
      const selection = $getSelection();
      savedSelectionRef.current = $isRangeSelection(selection) ? selection.clone() : null;
    });
    setOpen(true);
    setTabValue(0);
    setImageUrl('');
    setSelectedFile(null);
    setError('');
  };

  const handleClose = () => {
    setOpen(false);
    setImageUrl('');
    setSelectedFile(null);
    setError('');
    setUploading(false);
    savedSelectionRef.current = null;
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // 驗證文件類型
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      if (!validTypes.includes(file.type)) {
        setError('只允許上傳圖片檔案 (JPG, PNG, GIF, WEBP)');
        return;
      }
      // 驗證文件大小 (10MB)
      if (file.size > 10 * 1024 * 1024) {
        setError('圖片大小不能超過 10MB');
        return;
      }
      setSelectedFile(file);
      setError('');
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setError('請選擇要上傳的圖片');
      return;
    }

    setUploading(true);
    setError('');

    try {
      const formData = new FormData();
      formData.append('image', selectedFile);

      const response = await axios.post(`${url.backendHost}api/images/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        withCredentials: true
      });

      if (response.data && response.data.url) {
        // 將相對路徑轉換為完整 URL
        let imageUrl = response.data.url;
        
        if (!imageUrl.startsWith('http://') && !imageUrl.startsWith('https://')) {
          // 如果不是相對路徑也不是完整 URL，可能是相對路徑但沒有前導斜線
          imageUrl = `${url.backendHost}${imageUrl}`;
        }
        
        
        console.log('圖片上傳成功，URL:', imageUrl);
        
        insertImage(imageUrl);
      } else {
        setError('上傳失敗：未收到圖片連結');
      }
    } catch (err) {
      console.error('圖片上傳錯誤:', err);
      
      // 處理 413 錯誤（檔案過大）
      if (err.response?.status === 413) {
        setError('上傳圖片需小於 1MB');
      } else {
        const errorMessage = err.response?.data?.message || err.response?.data?.error || err.message || '圖片上傳失敗，請稍後再試';
        setError(errorMessage);
      }
    } finally {
      setUploading(false);
    }
  };

  const insertImage = (urlToInsert) => {
    if (urlToInsert) {
      const savedSelection = savedSelectionRef.current;
      editor.update(() => {
        let selection = $getSelection();
        if (!$isRangeSelection(selection) && savedSelection) {
          selection = savedSelection.clone();
          $setSelection(selection);
        }
        if ($isRangeSelection(selection)) {
          selection.insertNodes([$createImageNode(urlToInsert)]);
        }
      });
      handleClose();
    }
  };

  // 驗證圖片 URL 是否有效（用於 URL 輸入模式）
  const validateImageUrl = async (imageUrl) => {
    try {
      const response = await fetch(imageUrl, { method: 'HEAD' });
      return response.ok;
    } catch {
      return false;
    }
  };

  const handleInsertFromUrl = () => {
    if (imageUrl) {
      insertImage(imageUrl);
    } else {
      setError('請輸入圖片 URL');
    }
  };

  return (
    <>
      <button 
        onClick={handleOpen}
        className="toolbar-item spaced"
        aria-label="Insert Image"
        title="Insert Image"
      >
        <i className="format image" />
      </button>
      <Dialog open={open} onClose={handleClose} className="image-dialog" maxWidth="sm" fullWidth>
        <DialogTitle className="image-dialog-title">插入圖片</DialogTitle>
        <DialogContent className="image-dialog-content">
          <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)} sx={{ mb: 2 }}>
            <Tab label="本地上傳" />
            <Tab label="圖片 URL" />
          </Tabs>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          {tabValue === 0 && (
            <div>
              <input
                accept="image/*"
                style={{ display: 'none' }}
                id="image-upload-input"
                type="file"
                onChange={handleFileChange}
              />
              <label htmlFor="image-upload-input">
                <Button variant="outlined" component="span" fullWidth sx={{ mb: 2 }}>
                  選擇圖片檔案
                </Button>
              </label>
              {selectedFile && (
                <div style={{ marginBottom: '16px' }}>
                  <p style={{ margin: '8px 0', fontSize: '14px' }}>
                    已選擇: {selectedFile.name}
                  </p>
                  <p style={{ margin: '8px 0', fontSize: '12px', color: '#666' }}>
                    大小: {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                </div>
              )}
            </div>
          )}

          {tabValue === 1 && (
            <TextField
              autoFocus
              margin="dense"
              label="圖片 URL"
              type="text"
              fullWidth
              variant="outlined"
              value={imageUrl}
              onChange={(e) => setImageUrl(e.target.value)}
              className="image-url-input"
              placeholder="https://example.com/image.jpg"
            />
          )}
        </DialogContent>
        <DialogActions className="image-dialog-actions">
          <Button onClick={handleClose} disabled={uploading}>
            取消
          </Button>
          {tabValue === 0 ? (
            <Button 
              onClick={handleUpload} 
              variant="contained" 
              color="primary"
              disabled={!selectedFile || uploading}
            >
              {uploading ? <CircularProgress size={20} /> : '上傳並插入'}
            </Button>
          ) : (
            <Button 
              onClick={handleInsertFromUrl} 
              variant="contained" 
              color="primary"
              disabled={!imageUrl}
            >
              插入
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
}

export default ImagePlugin; 